/** schema.org JSON-LD blocks emitted by {@link SeoService}. */
export type JsonLdBlock = Record<string, unknown>;

export function buildArticleJsonLd(input: {
  headline: string;
  description?: string;
  articleBody?: string;
  datePublished: string;
  authorName: string;
  imageUrl?: string;
  url: string;
}): JsonLdBlock {
  const block: JsonLdBlock = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: input.headline,
    datePublished: input.datePublished,
    author: { '@type': 'Person', name: input.authorName },
    mainEntityOfPage: { '@type': 'WebPage', '@id': input.url },
  };

  if (input.description?.trim()) {
    block['description'] = input.description.trim();
  }
  if (input.articleBody?.trim()) {
    block['articleBody'] = input.articleBody.trim();
  }
  if (input.imageUrl?.trim()) {
    block['image'] = input.imageUrl.trim();
  }

  return block;
}

export function buildOrganizationJsonLd(input: {
  name: string;
  description?: string;
  imageUrl?: string;
  url: string;
}): JsonLdBlock {
  const block: JsonLdBlock = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: input.name,
    url: input.url,
  };

  if (input.description?.trim()) {
    block['description'] = input.description.trim();
  }
  if (input.imageUrl?.trim()) {
    block['logo'] = input.imageUrl.trim();
  }

  return block;
}

export function buildDefinedTermJsonLd(input: {
  name: string;
  description?: string;
  url: string;
}): JsonLdBlock {
  const block: JsonLdBlock = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: input.name,
    url: input.url,
    inDefinedTermSet: '',
  };
  if (input.description?.trim()) {
    block['description'] = input.description.trim();
  }
  return block;
}

export function buildDefinedTermSetJsonLd(input: {
  name: string;
  description?: string;
  url: string;
  terms: Array<{ name: string; description?: string; url: string }>;
}): JsonLdBlock {
  const block: JsonLdBlock = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: input.name,
    url: input.url,
    hasDefinedTerm: input.terms.map((term) => {
      const entry: JsonLdBlock = {
        '@type': 'DefinedTerm',
        name: term.name,
        url: term.url,
      };
      if (term.description?.trim()) {
        entry['description'] = term.description.trim();
      }
      return entry;
    }),
  };
  if (input.description?.trim()) {
    block['description'] = input.description.trim();
  }
  return block;
}

export function buildWebPageJsonLd(input: {
  name: string;
  description?: string;
  url: string;
  datePublished?: string;
  authorName?: string;
}): JsonLdBlock {
  const block: JsonLdBlock = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: input.name,
    url: input.url,
  };

  if (input.description?.trim()) {
    block['description'] = input.description.trim();
  }
  if (input.datePublished?.trim()) {
    block['datePublished'] = input.datePublished.trim();
  }
  if (input.authorName?.trim()) {
    block['author'] = { '@type': 'Person', name: input.authorName.trim() };
  }

  return block;
}
